import { booleanAttribute, Component, computed, input, model, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '@environments/environment';
import { ButtonComponent } from '@modules/components/button/button.component';
import { SelectComponent } from '@modules/components/select/select.component';
import { SelectOption } from '@modules/components/select/select.type';
import { PaginatorPage } from './paginator.type';

@Component({
  selector: 'app-paginator',
  imports: [ButtonComponent, MatIconModule, SelectComponent],
  templateUrl: './paginator.component.html',
  styleUrl: './paginator.component.scss',
  host: {
    role: 'navigation',
    'aria-label': 'Pagination',
    '[class.inline]': 'inline()',
  },
})
export class PaginatorComponent {
  length = input.required<number>();

  pageIndex = model(0);

  pageSize = model(environment.perPage);

  pageSizeOptions = input<number[]>([]);

  showFirstLast = input(true, { transform: booleanAttribute });

  inline = input(false, { transform: booleanAttribute });

  page = output<PaginatorPage>();

  protected sizeOptions = computed((): SelectOption<number>[] =>
    this.pageSizeOptions().map((size) => ({ value: size, label: String(size) })),
  );

  protected lastPageIndex = computed(() => Math.max(0, Math.ceil(this.length() / this.pageSize()) - 1));

  protected hasPrevious = computed(() => this.pageIndex() > 0);

  protected hasNext = computed(() => this.pageIndex() < this.lastPageIndex());

  protected range = computed(() => {
    const length = this.length();

    if (!length) return '0 of 0';

    const start = this.pageIndex() * this.pageSize();
    const end = Math.min(start + this.pageSize(), length);

    return `${start + 1} – ${end} of ${length}`;
  });

  protected goTo(pageIndex: number) {
    this.emit(pageIndex, this.pageSize());
  }

  protected changePageSize(pageSize: number | null) {
    if (pageSize === null || pageSize === this.pageSize()) return;

    const firstItemIndex = this.pageIndex() * this.pageSize();

    this.emit(Math.floor(firstItemIndex / pageSize), pageSize);
  }

  private emit(pageIndex: number, pageSize: number) {
    this.pageIndex.set(pageIndex);
    this.pageSize.set(pageSize);
    this.page.emit({ pageIndex, pageSize });
  }
}
